import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Stack, TextField, Alert, Box, Button } from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { post } from "aws-amplify/api";
import LoaderButton from "../components/LoaderButton";
import FormPage from "../components/FormPage";

const schema = Yup.object({
  Email: Yup.string()
    .required("Email is required")
    .matches(
      /^[a-zA-Z0-9.!#$%&’'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
      "Invalid email address"
    )
});

export default function RegisterUser() {
  const navigate = useNavigate();
  const [isBusy, setIsBusy] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");

  const handleSubmit = async (event) => {
    setIsBusy(true);
    setAlertMessage("");
    try {
      const op = post({
        apiName: "license",
        path: "/register-user",
        options: { body: { username: event.Email } }
      });
      await op.response;
      navigate(-1);
    } catch (e) {
      // Start from Amplify's own error message; enhance with body content if we can read it.
      let message = e?.message && e.message !== "Unknown error"
        ? e.message
        : "Registration failed";
      try {
        // Amplify v6 may have consumed the body stream before throwing —
        // race against a short timeout so we don't hang.
        const body = await Promise.race([
          e?.response?.body?.json?.(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
        // New structured shape: { error: "USER_ALREADY_EXISTS", message: "..." }
        if (body?.error === "USER_ALREADY_EXISTS") {
          message = body.message ?? "A user with that email is already registered.";
        } else if (body?.error === "SELF_REGISTRATION_DISABLED") {
          message = body.message ?? "Self-registration is not enabled for this licence — ask an admin.";
        } else if (body?.message) {
          message = body.message;
        } else if (typeof body === "string" && body) {
          // Old shape: bare string (json.dumps(str(e))). Detect common cases.
          if (/UsernameExistsException|already exists/i.test(body)) {
            message = "A user with that email is already registered.";
          } else {
            message = body;
          }
        }
      } catch (_) { /* keep the default message */ }
      setAlertMessage(message);
      setIsBusy(false);
    }
  };

  return (
    <FormPage
      title="Register a user"
      subtitle="Add a teammate to your organisation's licensed seats."
    >
      {alertMessage ? (
        <Alert severity="error" onClose={() => setAlertMessage("")}>{alertMessage}</Alert>
      ) : null}
      <Formik
        validateOnChange={false}
        validationSchema={schema}
        onSubmit={handleSubmit}
        initialValues={{ Email: "" }}
      >
        {({ handleSubmit, handleChange, values, errors }) => (
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <Stack spacing={2.5}>
              <TextField
                name="Email"
                label="Email"
                type="email"
                value={values.Email}
                onChange={handleChange}
                error={!!errors.Email}
                helperText={errors.Email}
                autoFocus
              />
              <Stack direction="row" spacing={1.5}>
                <LoaderButton
                  type="submit"
                  isLoading={isBusy}
                  text="Register"
                  loadingText="Registering…"
                  fullWidth
                />
                <Button onClick={() => navigate(-1)} variant="outlined" disabled={isBusy} fullWidth>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Formik>
    </FormPage>
  );
}
